import {useGrpcRequestMutation} from '@aboutyou/data-fetcher';
import {BasketPageService_ToggleCoinsLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.grpc.lazy';
import {ToggleCoinsResponse_EnabledHint} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/service.types';
import {useBasketToken, useCustomerSession} from '@aboutyou/user';
import {startTransition, useState} from 'react';
import {useReductionsStateRequestParameter} from '../../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useTargetGroupIdentifier} from '../../../../shared-deprecated/useTargetGroupIdentifier';
// eslint-disable-next-line jest/no-mocks-import
import {useUpdateBasketSectionsCallback} from '../../hooks/useUpdateBasketSections';

// Delay (after the API resolves) before the card-swap animation starts. Acts as
// the minimum time the loading state is shown before animating.
const MINIMUM_WAIT_BEFORE_ANIMATING = 800;
// Duration the "Preparing" card-swap step is shown before switching to "Applied".
const PREPARING_STEP_DURATION = 800;
// Duration of the product images animation in the "Applied" step
// (see RedeemCoinsPopupContent: unfold ends at 500ms delay + 650ms duration).
const APPLIED_STEP_ANIMATION_DURATION = 1150;
// The sheet auto-closes as soon as the product tiles are laid out,
// with a small buffer matching the app behavior.
const POPUP_AUTO_CLOSE_DELAY = APPLIED_STEP_ANIMATION_DURATION + 300;
// Delay before the error state is shown when the request fails.
const ERROR_STEP_DELAY = 4000;

export enum RedeemCoinsStep {
  Loading,
  Preparing,
  Applied,
  Error,
}

export const useRedeemCoins = () => {
  const {token: basketToken} = useBasketToken();

  const [step, setStep] = useState<RedeemCoinsStep | undefined>();
  const [isRedeemCoinsPopupShown, setShowRedeemCoinsPopup] = useState(false);
  const [redeemCoinsPopupData, setRedeemCoinsPopupData] = useState<
    ToggleCoinsResponse_EnabledHint | undefined
  >();
  const session = useCustomerSession();
  const updatePageBulkSections = useUpdateBasketSectionsCallback();
  const targetGroup = useTargetGroupIdentifier();
  const reductionsState = useReductionsStateRequestParameter();

  const delayedSetStatus = async (status: RedeemCoinsStep, delay: number) => {
    return new Promise<void>(resolve => {
      setTimeout(() => {
        setStep(status);
        resolve();
      }, delay);
    });
  };

  const redeemCoinsRequest = useGrpcRequestMutation(
    BasketPageService_ToggleCoinsLazy,
    {disabled: !session},
  );

  const redeemCoins = async (options: {
    toggleTransition: boolean;
    onError?: VoidFunction;
  }) => {
    if (!options.toggleTransition) {
      const {sections} = await redeemCoinsRequest.mutateAsync({
        session: session!,
        targetGroup,
        reductionsState,
        basketToken,
      });

      updatePageBulkSections(sections);

      return;
    }

    startTransition(() => {
      setStep(RedeemCoinsStep.Loading);
      setShowRedeemCoinsPopup(true);
    });

    try {
      const {sections, enabledHint} = await redeemCoinsRequest.mutateAsync({
        session: session!,
        targetGroup,
        reductionsState,
        basketToken,
      });

      setRedeemCoinsPopupData(enabledHint);
      await delayedSetStatus(
        RedeemCoinsStep.Preparing,
        MINIMUM_WAIT_BEFORE_ANIMATING,
      );
      updatePageBulkSections(sections);
      await delayedSetStatus(RedeemCoinsStep.Applied, PREPARING_STEP_DURATION);
    } catch (error) {
      await delayedSetStatus(RedeemCoinsStep.Error, ERROR_STEP_DELAY);
      options.onError?.();
    }

    // auto close the popup
    await new Promise<void>(resolve =>
      // eslint-disable-next-line no-promise-executor-return
      setTimeout(() => {
        setShowRedeemCoinsPopup(false);
        resolve();
      }, POPUP_AUTO_CLOSE_DELAY),
    );
  };

  return {
    redeemCoins,
    redeemCoinsPopupData,
    step,
    isRedeemCoinsPopupShown,
  };
};
